import React, { useState, useEffect, useCallback, useMemo } from 'react';
import UserLayout from '@/components/Layout/UserLayout.tsx';
import { capitalizeFirstLetter, calculatePercentageChangeFast } from '@einhaus/meteoric-helpers';
import type { UserPortfolioListItem } from '@einhaus/project314-types';
import CreatePortfolioModal from './components/CreatePortfolioModal';
import { useAuth } from '@/context/AuthContext';
import { FormCheckbox, DataTable, Button } from '@/components/UI';
import type { ColumnDef } from '@/components/UI/dataTableConfig';
import { Link } from 'react-router-dom';
import { showSpinner, hideSpinner } from '@/components/Layout/LoadingSpinner';
import { trpc } from '@/api/trpc.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useAppQuery } from '@/hooks/useAppQuery';
import { useTrpcMutation } from '@/api/useTrpcMutation';

type PortfolioListRow = UserPortfolioListItem;

const getPortfolioCurrentEquity = (portfolio: PortfolioListRow) => {
    return portfolio.balanceSummary?.currentEquity ?? portfolio.cashBalance + portfolio.positionsValue;
};

const getPortfolioLastClosingEquity = (portfolio: PortfolioListRow) => {
    return portfolio.balanceSummary?.lastClosingEquity ?? portfolio.lastClosingValue;
};

const getPortfolioTodayChangePercent = (portfolio: PortfolioListRow) => {
    return (
        portfolio.balanceSummary?.todayChangePercent ??
        calculatePercentageChangeFast(getPortfolioLastClosingEquity(portfolio), getPortfolioCurrentEquity(portfolio))
    );
};

const getRunningStrategyCount = (portfolio: PortfolioListRow): number =>
    portfolio.strategiesSubscribed?.filter((strategy) => strategy.effectiveActive).length ?? 0;

const getPortfolioEnabledStrategyCount = (portfolio: PortfolioListRow): number =>
    portfolio.strategiesSubscribed?.filter((strategy) => strategy.isActive).length ?? 0;

const getBlockedStrategyCount = (portfolio: PortfolioListRow): number =>
    portfolio.strategiesSubscribed?.filter((strategy) => strategy.isActive && strategy.inactiveReason === 'strategyInactive').length ?? 0;

const Portfolios = () => {
    // Access user data from AuthContext
    const { user } = useAuth();

    // State for UI
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Filter states
    const [hideInactive, setHideInactive] = useState(false);
    const [hidePaper, setHidePaper] = useState(false);
    const [hideNonBrokerage, setHideNonBrokerage] = useState(false);
    const [hideNoActiveStrategies, setHideNoActiveStrategies] = useState(false);

    // Query client for invalidation
    const queryClient = useQueryClient();

    // Fetch portfolios with tRPC
    const portfoliosQuery = useAppQuery(
        {
            ...trpc.user.fetchPortfolios.queryOptions(),
            refetchInterval: 30000, // Refetch every 30 seconds
            // Keep previous data while fetching new data to avoid UI flicker
            placeholderData: (previousData) => previousData
        },
        {
            fallbackErrorMessage: 'Failed to load portfolios',
            setError: (msg) => setError(msg)
        }
    );

    // Fetch trading accounts with tRPC
    const tradingAccountsQuery = useAppQuery(
        {
            ...trpc.user.fetchTradingAccounts.queryOptions(),
            refetchInterval: 30000, // Refetch every 30 seconds
            // Keep previous data while fetching new data to avoid UI flicker
            placeholderData: (previousData) => previousData
        },
        {
            fallbackErrorMessage: 'Failed to load trading accounts',
            setError: (msg) => setError(msg)
        }
    );

    // Manage global loading spinner based on query states - only for initial loading, not refetches
    useEffect(() => {
        // Only show spinner during initial loading, not during background refreshes
        const isInitialLoading = portfoliosQuery.isLoading || tradingAccountsQuery.isLoading;

        if (isInitialLoading) {
            showSpinner();
        } else {
            hideSpinner();
        }

        // Always hide spinner when component unmounts
        return () => {
            hideSpinner();
        };
    }, [portfoliosQuery.isLoading, tradingAccountsQuery.isLoading]);

    // Extract data from queries using useMemo to avoid dependency issues
    const portfolios = useMemo(() => portfoliosQuery.data?.portfolios ?? [], [portfoliosQuery.data?.portfolios]);
    const tradingAccounts = useMemo(() => tradingAccountsQuery.data?.tradingAccounts || [], [tradingAccountsQuery.data?.tradingAccounts]);
    const isRefreshing = portfoliosQuery.isFetching || tradingAccountsQuery.isFetching;

    // Load settings from user when available
    useEffect(() => {
        if (user?.settings) {
            setHideInactive(!!user.settings.hideInactivePortfolios);
            setHidePaper(!!user.settings.hidePaperPortfolios);
            setHideNonBrokerage(!!user.settings.hideNonBrokeragePortfolios);
            setHideNoActiveStrategies(!!user.settings.hideNoActiveStrategies);
        }
    }, [user]);

    // Setup mutation for saving user settings using the new useTrpcMutation pattern
    const saveSettingMutation = useTrpcMutation(trpc.user.saveUserSetting.mutationOptions(), {
        fallbackErrorMessage: 'Failed to save setting'
        // No success message needed for settings changes
    });

    // Handlers
    const handleRefresh = useCallback(() => {
        // Invalidate queries to trigger a refresh without showing global spinner
        // This will use the subtle loading indicator instead
        void queryClient.invalidateQueries({ queryKey: trpc.user.fetchPortfolios.queryOptions().queryKey });
        void queryClient.invalidateQueries({ queryKey: trpc.user.fetchTradingAccounts.queryOptions().queryKey });
    }, [queryClient]);

    const handleCreatePortfolio = () => {
        setShowCreateModal(true);
    };

    const saveUserSetting = (
        settingType: 'hideInactivePortfolios' | 'hidePaperPortfolios' | 'hideNonBrokeragePortfolios' | 'hideNoActiveStrategies',
        settingValue: boolean
    ) => {
        try {
            saveSettingMutation.mutate({
                settingType,
                settingValue
            });
        } catch (error: unknown) {
            console.error(`Error saving setting:`, error);
            // Don't show a toast for settings errors to avoid annoying the user
        }
    };

    const handleHideInactiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHideInactive(e.target.checked);
        saveUserSetting('hideInactivePortfolios', e.target.checked);
    };

    const handleHidePaperChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHidePaper(e.target.checked);
        saveUserSetting('hidePaperPortfolios', e.target.checked);
    };

    const handleHideNonBrokerageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHideNonBrokerage(e.target.checked);
        saveUserSetting('hideNonBrokeragePortfolios', e.target.checked);
    };

    const handleHideNoActiveStrategiesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setHideNoActiveStrategies(e.target.checked);
        saveUserSetting('hideNoActiveStrategies', e.target.checked);
    };

    // Filter portfolios based on current filter settings and search term
    const filteredPortfolios = useMemo(() => {
        if (!portfolios) return [];

        return portfolios.filter((portfolio) => {
            // Apply filter settings
            if (hideInactive && !portfolio.active) return false;
            if (hidePaper && portfolio.type === 'paper') return false;
            if (hideNonBrokerage && !portfolio.tradingAccountId) return false;
            if (hideNoActiveStrategies && getRunningStrategyCount(portfolio) === 0) return false;

            return true;
        });
    }, [portfolios, hideInactive, hidePaper, hideNonBrokerage, hideNoActiveStrategies]);

    // Error handling
    if (error) {
        return (
            <UserLayout containerSize="2000px" title="Portfolios - Error - Project 314">
                <div className="bg-red-100 text-red-800 border border-red-200 p-4 mb-4 rounded-md">Error: {error}</div>
            </UserLayout>
        );
    }

    const columns: ColumnDef<PortfolioListRow, unknown>[] = [
        {
            accessorKey: 'id',
            header: 'ID',
            size: 50
        },
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => {
                const p = info.row.original;

                const acct = p.tradingAccountId ? tradingAccounts.find((t) => t.id === p.tradingAccountId) : null;
                const isBrokerageDataStale = p.balanceSummary?.isBrokerageDataStale ?? false;
                const isAllocationLimited = p.balanceSummary?.isAllocationLimited ?? (p.balanceSummary?.allocationShortfall ?? 0) >= 0.01;
                const runningStrategyCount = getRunningStrategyCount(p);
                const portfolioEnabledStrategyCount = getPortfolioEnabledStrategyCount(p);
                const blockedStrategyCount = getBlockedStrategyCount(p);
                const hasNoRunningStrategies = p.active && portfolioEnabledStrategyCount > 0 && runningStrategyCount === 0;

                return (
                    <>
                        <div className="flex flex-wrap items-center gap-2">
                            <Link to={`/user/portfolio/${p.id}`}>{p.name}</Link>
                            {hasNoRunningStrategies ? (
                                <span className="whitespace-nowrap rounded border border-red-700/60 bg-red-500/10 px-1.5 py-0.5 text-xs font-medium text-red-200">
                                    No strategies running
                                </span>
                            ) : (
                                blockedStrategyCount > 0 && (
                                    <span className="whitespace-nowrap rounded border border-amber-700/60 bg-amber-500/10 px-1.5 py-0.5 text-xs font-medium text-amber-200">
                                        {blockedStrategyCount.toLocaleString()} blocked
                                    </span>
                                )
                            )}
                            {isBrokerageDataStale ? (
                                <span className="whitespace-nowrap rounded border border-red-700/60 bg-red-500/10 px-1.5 py-0.5 text-xs font-medium text-red-200">
                                    Broker data stale
                                </span>
                            ) : (
                                isAllocationLimited && (
                                    <span className="whitespace-nowrap rounded border border-yellow-700/60 bg-yellow-500/10 px-1.5 py-0.5 text-xs font-medium text-yellow-200">
                                        Allocation limited
                                    </span>
                                )
                            )}
                        </div>
                        {acct && <div className="text-zinc-400 text-sm">{acct.name}</div>}
                    </>
                );
            },
            size: 350
        },
        {
            accessorKey: 'type',
            header: 'Type',
            cell: (info) => capitalizeFirstLetter(info.getValue<string>()),
            size: 75
        },
        {
            id: 'activeStrategies',
            header: 'Running Strategies',
            accessorFn: getRunningStrategyCount,
            cell: (info) => `${info.getValue<number>()}`,
            size: 75
        },
        {
            id: 'positions',
            header: 'Positions',
            accessorFn: (p) => p.positions?.length ?? 0,
            cell: (info) => `${info.getValue<number>()}`,
            size: 75
        },
        {
            id: 'pendingTrades',
            header: 'Pending Trades',
            accessorFn: (p) => p.pendingTradeCount,
            cell: (info) => `${info.getValue<number>()}`,
            size: 75
        },
        {
            id: 'strategies',
            header: 'Strategies',
            accessorFn: (p) => p.strategiesSubscribed?.map((s) => s.name).join(', ') ?? '',
            cell: (info) => {
                const list = info.getValue<string>().split(', ').filter(Boolean);
                if (list.length === 0) return 'None';
                return (
                    <>
                        {list.map((name, index) => (
                            <div key={`${name}-${index}`}>{name}</div>
                        ))}
                    </>
                );
            },
            size: 350
        },
        {
            header: 'Current Equity',
            accessorFn: (p) => getPortfolioCurrentEquity(p),
            cell: (info) => `$${info.getValue<number>().toFixed(2)}`,
            size: 150
        },
        {
            header: '% Change Today',
            accessorFn: (p) => getPortfolioTodayChangePercent(p),
            cell: (info) => {
                const pct = info.getValue<number>();
                const dir = pct > 0 ? 'up' : pct < 0 ? 'down' : 'neutral';

                const cls = dir === 'up' ? 'text-profit-green' : dir === 'down' ? 'text-red-500' : 'text-zinc-400';

                return <span className={cls}>{pct.toFixed(2)}%</span>;
            },
            size: 100
        }
    ];

    return (
        <UserLayout containerSize="2000px" title="Portfolios - Project 314">
            <div className="container mx-auto mt-3">
                <div className="flex flex-wrap justify-between items-center mb-3">
                    <div>
                        <h3 className="text-2xl font-bold text-white">My Portfolios</h3>
                    </div>
                    <div className="text-right flex items-center gap-2">
                        <Button
                            aria-label={isRefreshing ? 'Refreshing portfolios' : 'Refresh portfolios'}
                            className="flex items-center gap-1"
                            disabled={isRefreshing}
                            variant="outline-dark"
                            onClick={handleRefresh}
                        >
                            {isRefreshing ? (
                                <>
                                    <div className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-zinc-300 border-t-transparent" />
                                    <span>Refreshing</span>
                                </>
                            ) : (
                                <>
                                    <svg
                                        className="h-4 w-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                        />
                                    </svg>
                                </>
                            )}
                        </Button>
                        <Button to="/user/strategySubscriptions" variant="outline-dark">
                            Strategy Subscriptions
                        </Button>
                        <Button disabled={isRefreshing} variant="outline-dark" onClick={handleCreatePortfolio}>
                            Create Portfolio
                        </Button>
                    </div>
                </div>

                <div className="flex flex-wrap justify-end items-center w-full">
                    <span className="text-zinc-300 text-sm font-medium my-auto flex items-center mr-3">Hide:</span>
                    <FormCheckbox
                        checked={hideInactive}
                        className="ml-2"
                        id="hideInactive"
                        label="Inactive"
                        onChange={handleHideInactiveChange}
                    />
                    <FormCheckbox checked={hidePaper} className="ml-2" id="hidePaper" label="Paper" onChange={handleHidePaperChange} />
                    <FormCheckbox
                        checked={hideNonBrokerage}
                        className="ml-2"
                        id="hideNonBrokerage"
                        label="Non-brokerage"
                        onChange={handleHideNonBrokerageChange}
                    />
                    <FormCheckbox
                        checked={hideNoActiveStrategies}
                        className="ml-2"
                        id="hideNoActiveStrategies"
                        label="No Running Strategies"
                        onChange={handleHideNoActiveStrategiesChange}
                    />
                </div>

                <div className="relative">
                    {isRefreshing && (
                        <div className="absolute top-0 right-0 p-2 z-10">
                            <div
                                className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-primary border-t-transparent"
                                role="status"
                            >
                                <span className="sr-only">Refreshing...</span>
                            </div>
                        </div>
                    )}
                    <DataTable<PortfolioListRow>
                        search
                        sort
                        striped
                        columns={columns}
                        data={filteredPortfolios}
                        pagination={{ enabled: true, pageSize: 20 }}
                    />
                </div>
            </div>

            <CreatePortfolioModal show={showCreateModal} onHide={() => setShowCreateModal(false)} onSuccess={handleRefresh} />
        </UserLayout>
    );
};

export default Portfolios;
